import { logger } from "@/modules/logger";
import { DateTime } from "luxon";
import type {
  AppendAudioBlobArgs,
  AppendTranscriptArgs,
  AppendTranscriptSnapshotArgs,
  AudioCapturePersistence,
  AudioPersistenceScope,
  ClearAudioBlobsArgs,
  GetAudioBlobsArgs,
  HasAnyForScopeArgs,
  PersistedAudioCaptureState,
  RecordingSessionCompleteArgs,
  RecordingSessionStartArgs,
  RecordingSessionUpdateArgs,
  SaveCapturedAudioIdArgs,
  RecordingEngineType,
} from "./audio-capture-persistence";
import { AudioRecordingCoordinator } from "@/store/audio-capture/coordinator/audio-recording-coordinator";
import type { AudioRecordingSnapshot } from "@/store/audio-capture/coordinator/types";
import {
  AudioRecordingRepository,
  AudioRecordingCapturedAudioRepository,
  AudioRecordingMetadataRepository,
} from "@/domains/dexie-db/repositories";
import type { TimestampedAssemblyAITurnMessage } from "@/domains/dexie-db/audio-capture/types";

const DEFAULT_NOW = () => DateTime.now().toMillis();

const sortTranscripts = (transcripts: TimestampedAssemblyAITurnMessage[]) =>
  [...transcripts].sort((a, b) => {
    if (a.turn_order === b.turn_order) {
      return (a.timestamp ?? 0) - (b.timestamp ?? 0);
    }
    return a.turn_order - b.turn_order;
  });

const coerceEngine = (engine?: string): RecordingEngineType => {
  const normalized = engine?.toLowerCase();
  if (normalized === "web") return "web";
  if (normalized === "desktop") return "desktop";
  return "unknown";
};

export class CoordinatorAudioCapturePersistence implements AudioCapturePersistence {
  constructor(
    private readonly coordinator: AudioRecordingCoordinator,
    private readonly repository: AudioRecordingRepository,
    private readonly runsRepository: AudioRecordingMetadataRepository,
    private readonly capturedAudioRepository: AudioRecordingCapturedAudioRepository,
    private readonly now: () => number = DEFAULT_NOW
  ) {}

  async clearAll(): Promise<void> {
    await this.repository.clear();
  }

  async clearByScope({ noteId, recordingId }: AudioPersistenceScope): Promise<void> {
    if (recordingId) {
      await this.repository.softDeleteByRecordingId(recordingId, this.now());
      return;
    }
    if (noteId) {
      await this.repository.softDeleteByNoteId(noteId, this.now());
      return;
    }

    logger.warn({
      message: "[CoordinatorAudioCapturePersistence] clearByScope called without identifiers",
    });
    await this.clearAll();
  }

  async hasAnyForScope({ noteId }: HasAnyForScopeArgs): Promise<boolean> {
    if (!noteId) return false;
    return this.repository.hasAnyForNote({ noteId });
  }

  async startSession({
    noteId,
    recordingId,
    capturedAudioId,
    engine,
    isRecordingWithoutTranscription,
    clearExisting,
    startedAt,
  }: RecordingSessionStartArgs): Promise<void> {
    if (clearExisting && noteId) {
      await this.repository.softDeleteByNoteId(noteId, this.now());
    }

    await this.coordinator.startRecording({
      recordingId,
      noteId,
      capturedAudioId,
      engine,
      isRecordingWithoutTranscription,
      createdAt: startedAt,
    });
  }

  async updateSession({
    recordingId,
    totalCapturedMs,
    pausedAccumulatedMs,
    status,
    silenceSince,
    autoStopReason,
    uploadProgressPercent,
  }: RecordingSessionUpdateArgs): Promise<void> {
    const run = await this.repository.getRecordingMetadata({ recordingId });
    if (!run) {
      logger.warn({
        message: "[CoordinatorAudioCapturePersistence] updateSession missing run",
        info: { recordingId },
      });
      return;
    }

    const now = this.now();
    const next = { ...run };
    let changed = false;

    if (typeof totalCapturedMs === "number") {
      next.totalCapturedMs = totalCapturedMs;
      changed = true;
    }

    if (typeof pausedAccumulatedMs === "number") {
      next.pausedAccumulatedMs = pausedAccumulatedMs;
      changed = true;
    }

    if (status) {
      next.status = status;
      if (status === "RECORDING" && next.startedAt == null) {
        next.startedAt = now;
      }
      if (status === "RECORDING") {
        next.lastResumedAt = now;
      }
      if (status === "PAUSED") {
        next.lastResumedAt = null;
      }
      changed = true;
    }

    if (silenceSince !== undefined) {
      next.silenceSince = silenceSince;
      changed = true;
    }

    if (autoStopReason !== undefined) {
      next.autoStopReason = autoStopReason;
      changed = true;
    }

    if (uploadProgressPercent !== undefined) {
      next.upload = {
        state: uploadProgressPercent == null ? "NOT_STARTED" : "IN_PROGRESS",
        progressPercent: uploadProgressPercent,
        error: null,
        updatedAt: now,
      };
      changed = true;
    }

    if (!changed) return;

    next.lastUpdatedAt = now;
    await this.repository.saveRecordingMetadata({ run: next, createdAt: now });
  }

  async completeSession({ recordingId, completedAt }: RecordingSessionCompleteArgs): Promise<void> {
    const run = await this.repository.getRecordingMetadata({ recordingId });
    if (!run) return;
    const now = completedAt ?? this.now();
    const next = {
      ...run,
      status: "STOPPED",
      lastUpdatedAt: now,
    };
    await this.repository.saveRecordingMetadata({ run: next, createdAt: now });
  }

  async saveCapturedAudioId({
    noteId,
    recordingId,
    capturedAudioId,
    createdAt,
  }: SaveCapturedAudioIdArgs): Promise<void> {
    if (!recordingId) return;
    await this.repository.saveCapturedAudioId({
      noteId,
      recordingId,
      capturedAudioId,
      createdAt,
    });
  }

  async appendTranscript({
    recordingId,
    transcript,
    channel,
    createdAt,
  }: AppendTranscriptArgs): Promise<void> {
    if (!recordingId) return;
    await this.coordinator.appendTranscriptTurn({
      recordingId,
      channel: channel ?? 0,
      transcript,
    });
    if (createdAt) {
      await this.updateSession({ recordingId, totalCapturedMs: undefined });
    }
  }

  async appendTranscriptSnapshot({
    recordingId,
    snapshot,
    channel,
    createdAt,
  }: AppendTranscriptSnapshotArgs): Promise<void> {
    if (!recordingId) return;
    const transcripts = Array.from(snapshot.transcriptMap.values());
    await this.coordinator.appendTranscriptSnapshot({
      recordingId,
      channel: channel ?? 0,
      transcripts,
      snapshotTime: snapshot.snapshotTime,
    });
    if (createdAt) {
      await this.updateSession({ recordingId, totalCapturedMs: undefined });
    }
  }

  async appendAudioBlob({ recordingId, blob }: AppendAudioBlobArgs): Promise<void> {
    if (!recordingId) return;
    await this.coordinator.appendAudioChunk({ recordingId, blob });
  }

  async getAudioBlobs({ noteId, recordingId }: GetAudioBlobsArgs): Promise<Blob[]> {
    const targetRecordingId = recordingId ?? (await this.resolveRecordingId(noteId));
    if (!targetRecordingId) return [];
    const blobs = await this.repository.getAudioBlobs({ recordingId: targetRecordingId });
    return blobs.map(blob => blob.blobData);
  }

  async clearAudioBlobs({ noteId, recordingId }: ClearAudioBlobsArgs): Promise<void> {
    const targetRecordingId = recordingId ?? (await this.resolveRecordingId(noteId));
    if (!targetRecordingId) return;
    await this.repository.clearAudioBlobs({ recordingId: targetRecordingId });
  }

  async getLatestState(): Promise<PersistedAudioCaptureState | undefined> {
    const runs = await this.runsRepository.list({ limit: 1, order: "desc" });
    let snapshot: AudioRecordingSnapshot | null = null;

    if (runs.length > 0) {
      snapshot = await this.coordinator.loadRecording({ recordingId: runs[0].recordingId });
    } else {
      const latestCaptured = await this.capturedAudioRepository.getLatest();
      if (latestCaptured?.recordingId) {
        snapshot = await this.coordinator.loadRecording({
          recordingId: latestCaptured.recordingId,
        });
      }
    }

    if (!snapshot || !snapshot.metadata || !snapshot.noteId || !snapshot.capturedAudioId) {
      return undefined;
    }

    return {
      noteId: snapshot.noteId,
      capturedAudioId: snapshot.capturedAudioId,
      recordingId: snapshot.recordingId,
      engine: coerceEngine(snapshot.metadata.engine),
      transcripts: snapshot.channels.map(channel => channel.transcriptChunks),
      transcriptSnapshots: snapshot.channels.map(channel =>
        channel.snapshots.map(snap => ({
          transcriptMap: new Map(
            sortTranscripts(snap.transcripts).map(transcript => [transcript.turn_order, transcript])
          ),
          snapshotTime: snap.snapshotTime,
        }))
      ),
      audioBlobs: snapshot.audioBlobs.map(blob => blob.blobData),
    };
  }

  private async resolveRecordingId(noteId?: string): Promise<string | undefined> {
    if (!noteId) return undefined;
    const runs = await this.runsRepository.list({ order: "desc" });
    const run = runs.find(entry => entry.noteId === noteId);
    if (run) return run.recordingId;
    const captured = await this.capturedAudioRepository.getLatestByNoteId(noteId);
    if (!captured?.recordingId) return undefined;
    const capturedRun = await this.runsRepository.getByRecordingId(captured.recordingId);
    if (capturedRun?.softDeletedAt != null) return undefined;
    return captured.recordingId;
  }
}
